import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CashFlowTableRow } from "@common/dto/reports/cash-flow-tab-report-response.dto";
import {
    CashFlowSectionId,
    NON_EXPANDABLE_CASH_FLOW_SECTIONS,
} from "@common/constants/reports/cashflow.constant";
import { ChildrenProps } from "../../types";
import {
    CashFlowExpandContext,
    CashFlowExpandContextValue,
} from "./cash-flow-expand.context";

interface Props extends ChildrenProps {
    rows: CashFlowTableRow[];
}

function collectRowIds(
    rows: CashFlowTableRow[],
    predicate: (row: CashFlowTableRow) => boolean,
): string[] {
    return rows.flatMap((row) => {
        const ids: string[] = [];

        if (predicate(row)) {
            ids.push(row.id);
        }

        if (row.children) {
            ids.push(...collectRowIds(row.children, predicate));
        }

        return ids;
    });
}

const isExpandableRow = (row: CashFlowTableRow): boolean =>
    (row.children?.length ?? 0) > 0 &&
    !NON_EXPANDABLE_CASH_FLOW_SECTIONS.includes(row.id as CashFlowSectionId);

const isDefaultExpandedRow = (row: CashFlowTableRow): boolean =>
    row.isExpanded === true;

export const CashFlowExpandProvider: React.FC<Props> = ({ rows, children }) => {
    const allExpandableRowIds = useMemo(
        () => new Set(collectRowIds(rows, isExpandableRow)),
        [rows],
    );

    const defaultExpandedRowIds = useMemo(
        () => new Set(collectRowIds(rows, isDefaultExpandedRow)),
        [rows],
    );

    const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(
        defaultExpandedRowIds,
    );

    useEffect(
        () => setExpandedRowIds(defaultExpandedRowIds),
        [defaultExpandedRowIds],
    );

    const expandAll = useCallback(
        () => setExpandedRowIds(allExpandableRowIds),
        [allExpandableRowIds],
    );

    const collapseAll = useCallback(() => setExpandedRowIds(new Set()), []);

    const toggleExpand = useCallback((rowId: string) => {
        setExpandedRowIds((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) {
                next.delete(rowId);
            } else {
                next.add(rowId);
            }
            return next;
        });
    }, []);

    const isAllCollapsed = expandedRowIds.size === 0;

    const value = useMemo<CashFlowExpandContextValue>(
        () => ({
            expandedRowIds,
            isAllCollapsed,
            expandAll,
            collapseAll,
            toggleExpand,
        }),
        [expandedRowIds, isAllCollapsed, expandAll, collapseAll, toggleExpand],
    );

    return (
        <CashFlowExpandContext.Provider value={value}>
            {children}
        </CashFlowExpandContext.Provider>
    );
};
